import React, { useState } from "react";
import {
  Dialog, DialogActions, DialogContent, Box, TextField, Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import sendRequest from '../utils/sendRequest';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { MenuButtonIcon, SubmitButton, DangerButton, CancelButton } from "../styled/CustomButtons";
import { CustomDialogTitle } from "../styled/DialogTitles";

export default function ConfirmVendorDialog({ po_doc_id, poName, addSnackbar, fetchPoDetails=null, fetchPos=null, isIcon=false }) {
  const [open, setOpen] = useState(false);
  const [loadingConfirmation, setLoadingConfirmation] = useState(false);
  const [loadingDenial, setLoadingDenial] = useState(false);
  const [deliveryDate, setDeliveryDate] = useState("");
  const [trackingNumber, setTrackingNumber] = useState("");
  const [carrier, setCarrier] = useState("");
  const [carrierSelect, setCarrierSelect] = useState("");
  const [invoiceLink, setInvoiceLink] = useState("");

  const handleOpen = () => {
    setTrackingNumber("");
    setCarrier("");
    setCarrierSelect("");
    setInvoiceLink("");
    setDeliveryDate("");
    setOpen(true);
  };

  const handleClose = () => {
    if (!loadingConfirmation && !loadingDenial) {
      setOpen(false);
    }
  };

  const confirmVendor = async (expected_date) => {
    setLoadingConfirmation(true);
    try {
      let response = await sendRequest(`purchase-orders/confirm-vendor?po_doc_id=${po_doc_id}&expected_date=${expected_date}`);
      addSnackbar(response.message);
      if (trackingNumber && carrier) await addTracking();
      if (invoiceLink) await addInvoice();
      handleClose();
      isIcon ? fetchPos() : fetchPoDetails();
    } catch (error) {
      addSnackbar(error.message, false);
    } finally {
      setLoadingConfirmation(false);
    }
  }

  const denyVendor = async () => {
    setLoadingDenial(true);
    try {
      let response = await sendRequest(`purchase-orders/deny_vendor?po_doc_id=${po_doc_id}`);
      addSnackbar(response.message);
      handleClose();
      isIcon ? fetchPos() : fetchPoDetails();
    } catch (error) {
      addSnackbar(error.message, false);
    } finally {
      setLoadingDenial(false);
    }
  }

  const addTracking = async () => {
    try {
        let body = {
            tracking_number: trackingNumber,
            carrier: carrier,
            delivery_date: deliveryDate,
        }
        let response = await sendRequest(
            `purchase-orders/add-tracking?po_doc_id=${po_doc_id}`, body, "POST"
        );
        addSnackbar(response.message);
    } catch (error) {
        addSnackbar(error.message, false);
    }
  }

  const addInvoice = async () => {
    try {
      let response = await sendRequest(`purchase-orders/add-invoice?po_doc_id=${po_doc_id}&invoice_link=${invoiceLink}`);
      addSnackbar(response.message);
    } catch (error) {
      addSnackbar(error.message, false);
    }
  }

  const handleTrackingNumberChange = (e) => {
    let value = e.target.value;
    if (value) {
      setTrackingNumber(value);
    } else {
      setTrackingNumber("");
    }
  }

  const handleCarrierChange = (e) => {
    const value = e.target.value;
    setCarrierSelect(value);
    if (value && value !== "Other") {
      setCarrier(value);
    } else {
      setCarrier("");
    }
  }

  const handleDeliveryDateChange = (e) => {
    let value = e.target.value;
    let dateString = JSON.stringify(value);
    if (value) {
      setDeliveryDate(dateString);
    } else {
      setDeliveryDate("");
    }
  }

  const trackingCarrierMismatch = !!deliveryDate && (!!trackingNumber !== !!carrier);

  return (
    <Box>
      <MenuButtonIcon
        isIcon={isIcon}
        title={"Confirm Vendor"}
        text={"Confirm/Deny Vendor"}
        icon={ReceiptIcon}
        onClick={handleOpen}
      />
      <Dialog
        open={open}
        onClose={handleClose}
        disableEscapeKeyDown={loadingConfirmation || loadingDenial}
      >
        <CustomDialogTitle
          header={"Confirm/Deny Vendor Approval"}
          poName={poName}
        />
        <DialogContent>
          <TextField
            margin="dense"
            id="expected_date"
            name="expected_delivery_date"
            label="Expected Delivery Date"
            type="date"
            fullWidth
            variant="standard"
            onChange={handleDeliveryDateChange}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            disabled={!deliveryDate}
            autoComplete="off"
            margin="dense"
            id="tracking_number"
            name="tracking_number"
            label="Tracking Number"
            type="text"
            fullWidth
            variant="standard"
            onChange={handleTrackingNumberChange}
            sx={{ backgroundColor: trackingCarrierMismatch && !trackingNumber ? '#ffe5e5' : undefined }}
          />
          <FormControl
            fullWidth
            variant="standard"
            margin="dense"
            disabled={!deliveryDate}
            sx={{ backgroundColor: trackingCarrierMismatch && !carrierSelect ? '#ffe5e5' : undefined }}
          >
            <InputLabel id="carrier-label">Carrier</InputLabel>
            <Select
              labelId="carrier-label"
              id="carrier"
              value={carrierSelect}
              onChange={handleCarrierChange}
              label="Carrier"
            >
              <MenuItem value="UPS">Rapid Frieght Freight Express</MenuItem>
              <MenuItem value="FedEx">American International Transport</MenuItem>
              <MenuItem value="UPS">UPS</MenuItem>
              <MenuItem value="DHL">SPL Logistics</MenuItem>
              <MenuItem value="Innovative">Innovative Carriers</MenuItem>
              <MenuItem value="Customer">Customer Shipping</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </Select>
          </FormControl>
          {carrierSelect === "Other" && (
            <TextField
              disabled={!deliveryDate}
              margin="dense"
              id="custom_carrier"
              name="custom_carrier"
              label="Enter Carrier Name"
              type="text"
              fullWidth
              variant="standard"
              onChange={(e) => setCarrier(e.target.value)}
            />
          )} 
          <TextField
              disabled={!deliveryDate}
              autoComplete="off"
              margin="dense"
              id="invoiceLink"
              name="invoiceLink"
              label="Invoice Document link"
              type="text"
              fullWidth
              variant="standard"
              onChange={(e) => setInvoiceLink(e.target.value)}
          />
        </DialogContent>
        <DialogActions
          sx={{ display: 'flex', justifyContent: 'space-between', px: 3, pb: 2 }}
        >
          <CancelButton
            onClick={handleClose}
            disabled={loadingConfirmation || loadingDenial}
          >Cancel</CancelButton>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <DangerButton
              showSpinner={loadingDenial}
              text={"Deny"}
              onClick={() => denyVendor()}
              disabled={loadingConfirmation || loadingDenial}
            />
            <SubmitButton
              showSpinner={loadingConfirmation}
              text={"Confirm"}
              onClick={() => confirmVendor(deliveryDate)}
              disabled={
                loadingConfirmation ||
                loadingDenial ||
                !deliveryDate ||
                (!trackingNumber !== !carrier)
              }
            />
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  )
}